import { useMemo } from "react";
import { useTable, useFlexLayout, useResizeColumns } from "react-table";
import { TabComponentProps } from "./tabRegistry";
import TableCustomLoader from "src/components/Common/TableCustomLoader";
import Pagination from "src/components/Common/Pagination";
import useIsMobile from "src/hooks/useIsMobile";

const COLUMNS_CONFIG = [
  { id: "line_item_name",   header: "Line Item",        minWidth: 150, width: 150, maxWidth: 400 },
  { id: "creative_name",    header: "Creative",         minWidth: 150, width: 150, maxWidth: 400 },
  { id: "start_date",       header: "Start Date",       minWidth: 120, width: 120, maxWidth: 120 },
  { id: "end_date",         header: "End Date",         minWidth: 120, width: 120, maxWidth: 120 },
  { id: "impressions",      header: "Impressions",      minWidth: 100, width: 100, maxWidth: 150 },
  { id: "clicks",           header: "Clicks",           minWidth: 100, width: 100, maxWidth: 150 },
  { id: "conversions",      header: "Conversions",      minWidth: 100, width: 100, maxWidth: 150 },
  { id: "completed_views",  header: "Completed Views",  minWidth: 120, width: 120, maxWidth: 160 },
  { id: "estimate_spend",   header: "Estimated Spend",  minWidth: 120, width: 120, maxWidth: 160 },
  { id: "ctr",              header: "CTR",              minWidth: 80,  width: 80,  maxWidth: 120 },
  { id: "completion_rate",  header: "Completion Rate",  minWidth: 120, width: 120, maxWidth: 160 },
];

const CreativeTable = ({
  rows: dataRows,
  isLoading,
  currentPage,
  totalPages,
  totalRecords,
  perPage,
  sortColumn,
  sortDirection,
  exchangeId,
  onSortChange,
  onPageChange,
  onPerPageChange,
  visibleColumns,
}: TabComponentProps) => {
  const isMobile = useIsMobile();

  const tableData = useMemo(() => dataRows || [], [dataRows]);

  // Show columns in visibleColumns order, falling back to COLUMNS_CONFIG order
  const columns = useMemo(() => {
    if (!tableData.length) return [];

    const availableKeys = new Set<string>();
    tableData.forEach((row) => {
      Object.keys(row).forEach((key) => availableKeys.add(key));
    });

    // If visibleColumns provided, use its order; otherwise show all available
    const orderedConfig = visibleColumns
      ? visibleColumns
          .map((id) => COLUMNS_CONFIG.find((col) => col.id === id))
          .filter((col): col is (typeof COLUMNS_CONFIG)[number] => col != null)
      : COLUMNS_CONFIG;

    return orderedConfig
      .filter((col) => availableKeys.has(col.id))
      .map((col) => ({
        Header: col.header,
        accessor: col.id,
        id: col.id,
        minWidth: col.minWidth,
        width: col.width,
        maxWidth: col.maxWidth,
        Cell: ({ value }: { value: any }) => {
          const display = String(value ?? "");
          if (col.id === "line_item_name") {
            return (
              <div className="line-item-ellipsis" title={display}>
                {display}
              </div>
            );
          }
          return (
            <div className="ellipsis-cell" title={display}>
              {display}
            </div>
          );
        },
      }));
  }, [tableData, visibleColumns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: tableData,
      disableSortBy: true,
      manualSortBy: true,
    },
    useFlexLayout,
    useResizeColumns
  );

  if (isLoading) {
    return <TableCustomLoader length={1} message="Loading creative data..." />;
  }

  // visibleColumns=[] means user deselected all metrics
  if (Array.isArray(visibleColumns) && visibleColumns.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center no-records no-records-min-height">
        No data available
      </div>
    );
  }

  if (!dataRows || dataRows.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center no-records no-records-min-height"
      >
        No creative data available
      </div>
    );
  }

  return (
    <div className="table-container">
      {!isMobile ? (
        <div className="table-wrapper custom-scrollbar">
          <table {...getTableProps()} className="table table-bordered exchange-table">
            <thead>
              {headerGroups.map((headerGroup: any) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={`${exchangeId}-cr-hg`}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      {...column.getHeaderProps()}
                      key={`${exchangeId}-cr-${column.id}`}
                      className="sortable-header"
                      onClick={() => onSortChange(column.id)}
                      style={{
                        ...column.getHeaderProps().style,
                        cursor: "pointer",
                      }}
                    >
                      {column.render("Header")}
                      {sortColumn === column.id && (
                        <i
                          className={`fas ${
                            sortDirection === "desc"
                              ? "fa-sort-down inline-sort-icon-desc"
                              : "fa-sort-up inline-sort-icon-asc"
                          }`}
                        ></i>
                      )}
                      {column.canResize && (
                        <div
                          {...column.getResizerProps()}
                          className="resizer"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row: any) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={`${exchangeId}-cr-row-${row.id}`}>
                    {row.cells.map((cell: any) => (
                      <td {...cell.getCellProps()} key={`${exchangeId}-cr-${row.id}-${cell.column.id}`}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mobile-cards">
          {dataRows.map((row, rowIndex) => (
            <div className="mobile-card card" key={`${exchangeId}-cr-card-${rowIndex}`}>
              {(visibleColumns
                ? visibleColumns
                    .map((id) => COLUMNS_CONFIG.find((c) => c.id === id))
                    .filter((c): c is (typeof COLUMNS_CONFIG)[number] => c != null)
                : COLUMNS_CONFIG
              ).filter((col) => col.id in row).map((col) => (
                <div className="mobile-card-row" key={`${exchangeId}-cr-card-${rowIndex}-${col.id}`}>
                  <span className="row-title">{col.header}:</span>
                  <span className="row-value">{String(row[col.id] ?? "")}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <Pagination
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={onPageChange}
        currentCount={rows.length}
        pageSize={perPage}
        onPageSizeChange={onPerPageChange}
      />
    </div>
  );
};

export default CreativeTable;
